import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  REQUEST_KINDS,
  REQUEST_KIND_MAP,
  type RequestKind,
} from '@goyone/shared';
import {
  Badge,
  ErrorState,
  Modal,
  Notice,
  PageHead,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import type { RequestTypeRow } from '../hr/Requests';

/**
 * قواعدُ الطلبات.
 *
 * لائحةٌ داخليّةٌ تُكتب هنا لا في الشيفرة: شركةٌ تسمح بإذنين في الشهر
 * وأخرى بأربعة، وشركةٌ تشترط الإيصال على المصروفات وأخرى لا. ورفعُ سقف
 * الأذونات قرارٌ إداريٌّ لا يجوز أن يمرّ على مبرمج.
 *
 * والإيقافُ لا الحذف: نوعٌ عليه طلباتٌ سابقةٌ تاريخٌ لا يُمحى.
 */
export function RequestTypes() {
  const [editing, setEditing] = useState<RequestTypeRow | 'new' | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'request-types'],
    queryFn: () => api<RequestTypeRow[]>('/hr/request-types'),
  });

  const rows = list.data ?? [];

  return (
    <div className="page">
      <PageHead
        title="قواعد الطلبات"
        subtitle="ما يُطلب في كلّ نوع، وحدودُه، ومن يبتّ فيه"
        actions={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={15} />
            نوع جديد
          </button>
        }
      />

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      <Notice title="الإجازات لا تُضبط هنا" icon="calendar">
        أنواعُ الإجازات وأرصدتُها في كتالوجٍ مستقلّ لأنّها تخصم رصيدًا وتدخل
        حسابَ المرتّب. وهذه الشاشةُ لما سواها.
      </Notice>

      {list.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {rows.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>النوع</th>
                  <th>ما يُطلب</th>
                  <th>الحدود</th>
                  <th>يبتّ فيه</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <span className="cell-strong">{t.label}</span>
                      <div className="tiny mute">{t.kindLabel}</div>
                    </td>
                    <td className="cell-mute">
                      <div className="rc-offer-facts">
                        {t.needsDates && <span>تاريخ</span>}
                        {t.needsHours && <span>ساعات</span>}
                        {t.needsAmount && <span>مبلغ</span>}
                        {t.needsReason && <span>سبب</span>}
                        {t.needsAttachment && <span>مرفق</span>}
                      </div>
                    </td>
                    <td className="cell-mute num">
                      <div className="rc-offer-facts">
                        {t.maxPerMonth !== null && (
                          <span>{t.maxPerMonth} شهريًّا</span>
                        )}
                        {t.maxHours !== null && (
                          <span>{t.maxHours} ساعات</span>
                        )}
                        {t.minNoticeDays !== null && (
                          <span>مهلة {t.minNoticeDays} يوم</span>
                        )}
                        {t.maxPerMonth === null &&
                          t.maxHours === null &&
                          t.minNoticeDays === null && <span>بلا حدود</span>}
                      </div>
                    </td>
                    <td className="cell-mute">
                      {t.approverRole === 'ADMIN' ? 'مدير النظام' : 'المدير'}
                    </td>
                    <td>
                      <Badge kind={t.isActive ? 'success' : 'neutral'}>
                        {t.isActive ? 'مفعَّل' : 'موقوف'}
                      </Badge>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm"
                        onClick={() => setEditing(t)}
                      >
                        تعديل
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <TypeDialog
          type={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function TypeDialog({
  type,
  onClose,
}: {
  type: RequestTypeRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [label, setLabel] = useState(type?.label ?? '');
  const [kind, setKind] = useState<RequestKind>(type?.kind ?? 'PERMISSION');
  const [description, setDescription] = useState(type?.description ?? '');
  const [needsDates, setNeedsDates] = useState(type?.needsDates ?? false);
  const [needsHours, setNeedsHours] = useState(type?.needsHours ?? false);
  const [needsAmount, setNeedsAmount] = useState(type?.needsAmount ?? false);
  const [needsReason, setNeedsReason] = useState(type?.needsReason ?? true);
  const [needsAttachment, setNeedsAttachment] = useState(
    type?.needsAttachment ?? false,
  );
  const [maxPerMonth, setMaxPerMonth] = useState(
    type?.maxPerMonth !== null && type?.maxPerMonth !== undefined
      ? String(type.maxPerMonth)
      : '',
  );
  const [maxHours, setMaxHours] = useState(
    type?.maxHours !== null && type?.maxHours !== undefined
      ? String(type.maxHours)
      : '',
  );
  const [minNoticeDays, setMinNoticeDays] = useState(
    type?.minNoticeDays !== null && type?.minNoticeDays !== undefined
      ? String(type.minNoticeDays)
      : '',
  );
  const [approverRole, setApproverRole] = useState(
    type?.approverRole ?? 'MANAGER',
  );
  const [isActive, setIsActive] = useState(type?.isActive ?? true);

  /* اختيارُ النوع يقترح حقولَه — ويُعدَّل بعده. */
  const applyKind = (next: RequestKind) => {
    setKind(next);
    if (type) return;
    const s = REQUEST_KIND_MAP[next].suggests;
    setNeedsDates(s.needsDates);
    setNeedsHours(s.needsHours);
    setNeedsAmount(s.needsAmount);
    setNeedsAttachment(s.needsAttachment);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        label: label.trim(),
        kind,
        description: description.trim() || null,
        needsDates,
        needsHours,
        needsAmount,
        needsReason,
        needsAttachment,
        maxPerMonth: maxPerMonth ? Number(maxPerMonth) : null,
        maxHours: maxHours ? Number(maxHours) : null,
        minNoticeDays: minNoticeDays ? Number(minNoticeDays) : null,
        approverRole,
        isActive,
        order: type?.order ?? 0,
      };
      return type
        ? api(`/hr/request-types/${type.id}`, { method: 'PATCH', body })
        : api('/hr/request-types', { method: 'POST', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast(type ? 'حُفظ النوع' : 'أُضيف النوع');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={type ? `تعديل ${type.label}` : 'نوع طلب جديد'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={label.trim().length < 2 || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="rt-label">
            اسم النوع
          </label>
          <input
            id="rt-label"
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="إذن انصراف"
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="rt-desc">
            وصفٌ يقرؤه الموظّف
          </label>
          <input
            id="rt-desc"
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <h3 className="card-title mt-4">التصنيف</h3>
      <div className="rc-kinds">
        {REQUEST_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            className={`rc-kind${kind === k ? ' is-active' : ''}`}
            aria-pressed={kind === k}
            onClick={() => applyKind(k)}
          >
            <strong>
              <Badge kind={REQUEST_KIND_MAP[k].kind}>
                {REQUEST_KIND_MAP[k].label}
              </Badge>
            </strong>
            <span className="tiny mute">
              {REQUEST_KIND_MAP[k].description}
            </span>
          </button>
        ))}
      </div>

      <h3 className="card-title mt-4">ما يُطلب في النموذج</h3>
      <div className="rc-offer-facts">
        <Toggle label="تاريخ" on={needsDates} set={setNeedsDates} />
        <Toggle label="ساعات" on={needsHours} set={setNeedsHours} />
        <Toggle label="مبلغ" on={needsAmount} set={setNeedsAmount} />
        <Toggle label="سبب" on={needsReason} set={setNeedsReason} />
        <Toggle label="مرفق" on={needsAttachment} set={setNeedsAttachment} />
      </div>

      <h3 className="card-title mt-4">الحدود</h3>
      <p className="tiny mute">
        الفارغُ يعني بلا حدّ. والعدُّ الشهريّ على المعلَّق والموافَق عليه —
        المسحوبُ والمرفوضُ لا يُحسبان.
      </p>

      <div className="form-grid mt-2">
        <div className="field">
          <label className="label" htmlFor="rt-max">
            الأقصى في الشهر
          </label>
          <input
            id="rt-max"
            className="input num"
            type="number"
            min={1}
            value={maxPerMonth}
            onChange={(e) => setMaxPerMonth(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="rt-hours">
            أقصى ساعات للطلب
          </label>
          <input
            id="rt-hours"
            className="input num"
            type="number"
            step="0.25"
            min={0.25}
            value={maxHours}
            onChange={(e) => setMaxHours(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="rt-notice">
            مهلة الطلب بالأيّام
          </label>
          <input
            id="rt-notice"
            className="input num"
            type="number"
            min={0}
            value={minNoticeDays}
            onChange={(e) => setMinNoticeDays(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="rt-approver">
            يبتّ فيه
          </label>
          <select
            id="rt-approver"
            className="select"
            value={approverRole}
            onChange={(e) => setApproverRole(e.target.value)}
          >
            <option value="MANAGER">المدير فما فوق</option>
            <option value="ADMIN">مدير النظام فما فوق</option>
          </select>
        </div>

        <div className="field">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            مفعَّل
          </label>
          <span className="tiny mute">
            الإيقافُ لا الحذف — نوعٌ عليه طلباتٌ سابقةٌ تاريخٌ لا يُمحى.
          </span>
        </div>
      </div>
    </Modal>
  );
}

function Toggle({
  label,
  on,
  set,
}: {
  label: string;
  on: boolean;
  set: (v: boolean) => void;
}) {
  return (
    <label className="row tiny" style={{ gap: 6 }}>
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => set(e.target.checked)}
      />
      {label}
    </label>
  );
}
